import { useEffect, useRef, useState } from "react";
import type { ComponentType } from "react";
import { AnimatePresence, motion } from "motion/react";
import type { SlideProps } from "@/engine/types";
import {
  Badge,
  ExplainerPanel,
  GlassCard,
  GradientText,
  Reveal,
  SlideShell,
  useAccent,
} from "@/ui";
import { SERP_REGIONS } from "./components/serpData";
import { RegionFrame } from "./components/RegionFrame";
import { ProgressDots } from "./components/ProgressDots";
import {
  AiOverviewBlock,
  LocalPack,
  OrganicHero,
  PeopleAlsoAsk,
  RelatedSearches,
  RichResult,
  SearchHeader,
  ShoppingCarousel,
  SponsoredAds,
} from "./components/SerpBlocks";

/** Visual block for each region, in SERP_REGIONS order (R1..R9). */
const BLOCKS: readonly ComponentType[] = [
  SearchHeader,
  SponsoredAds,
  ShoppingCarousel,
  AiOverviewBlock,
  OrganicHero,
  RichResult,
  PeopleAlsoAsk,
  LocalPack,
  RelatedSearches,
];

const REGION_COUNT = SERP_REGIONS.length;

const LESSON_LINES = [
  "There is no fixed recipe for “#1” — results depend on query, region, device, competition, and Google's quality systems.",
  "Featured snippets, maps, images, and ads change what “first” even means on the page.",
  "Measure business outcomes — qualified traffic, signups, revenue — not only a single position for a vanity keyword.",
] as const;

/** Fills the right column with his lesson until a region is selected. */
function LessonCard() {
  const [from] = useAccent();
  return (
    <GlassCard className="p-8">
      <p
        className="mb-2 text-xl font-medium uppercase tracking-widest"
        style={{ color: from }}
      >
        Real talk
      </p>
      <h2 className="font-display text-3xl font-semibold leading-snug text-white">
        SERP positions: realistic expectations
      </h2>
      <ul className="mt-5 space-y-4">
        {LESSON_LINES.map((line) => (
          <li key={line} className="flex items-start gap-3 text-xl leading-relaxed text-white/80">
            <span
              className="mt-3 h-2 w-2 shrink-0 rounded-full"
              style={{ backgroundColor: from }}
            />
            <span>{line}</span>
          </li>
        ))}
      </ul>
      <p className="mt-6 text-xl text-white/50">
        Click any numbered region of the mock results page ←
      </p>
    </GlassCard>
  );
}

/** c6-serp-demo — DEMO: SERP positions, realistic expectations. */
export function SerpDemoSlide({ active }: SlideProps) {
  const [selected, setSelected] = useState<number | null>(null);
  const [visited, setVisited] = useState<ReadonlySet<number>>(new Set());
  const regionEls = useRef<(HTMLDivElement | null)[]>([]);

  // Selecting a region marks it visited and scrolls it into view.
  useEffect(() => {
    if (selected === null) return;
    setVisited((prev) =>
      prev.has(selected) ? prev : new Set(prev).add(selected),
    );
    regionEls.current[selected]?.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
    });
  }, [selected]);

  // Close the panel when the overview opens (its Escape handler must yield).
  useEffect(() => {
    if (!active) setSelected(null);
  }, [active]);

  // "[" / "]" cycle regions — consumed, so the deck never sees them.
  useEffect(() => {
    if (!active) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "[" && e.key !== "]") return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      e.preventDefault();
      const dir = e.key === "]" ? 1 : -1;
      setSelected((prev) =>
        prev === null
          ? dir === 1
            ? 0
            : REGION_COUNT - 1
          : (prev + dir + REGION_COUNT) % REGION_COUNT,
      );
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [active]);

  const allVisited = visited.size === REGION_COUNT;
  const region = selected === null ? undefined : SERP_REGIONS[selected];

  return (
    <SlideShell chapter={6} kicker="Demo · Click any part of the page">
      <div className="relative min-h-0 flex-1">
        {/* Persistent demo-data disclaimer */}
        <div className="absolute right-0 top-0 z-20">
          <Badge>Demo data — all results invented</Badge>
        </div>

        {/* Mock SERP column */}
        <Reveal active={active} className="flex h-full w-[62%] min-w-0 flex-col">
          <ProgressDots
            visited={visited}
            selected={selected}
            onSelect={(i) => setSelected(i)}
          />
          <div className="mt-4 min-h-0 flex-1 overflow-hidden rounded-2xl border border-white/10 bg-[#202124] shadow-[0_12px_48px_rgba(0,0,0,0.5)]">
            <div className="h-full overflow-y-auto px-10 py-8">
              <div className="flex flex-col gap-4">
                {SERP_REGIONS.map((r, i) => {
                  const Block = BLOCKS[i];
                  if (!Block) return null;
                  return (
                    <RegionFrame
                      key={r.id}
                      n={r.n}
                      label={r.name}
                      selected={selected === i}
                      visited={visited.has(i)}
                      dimmed={selected !== null && selected !== i}
                      onSelect={() => setSelected(i)}
                      regionRef={(el) => {
                        regionEls.current[i] = el;
                      }}
                    >
                      <Block />
                    </RegionFrame>
                  );
                })}
              </div>
            </div>
          </div>
        </Reveal>

        {/* His lesson, parked on the right until a region is selected */}
        <AnimatePresence>
          {selected === null && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              transition={{ duration: 0.35, ease: [0.32, 0.72, 0, 1] }}
              className="absolute right-0 top-1/2 w-[33%] -translate-y-1/2"
            >
              <LessonCard />
            </motion.div>
          )}
        </AnimatePresence>

        {/* Right-docked explainer */}
        <ExplainerPanel
          open={region !== undefined}
          onClose={() => setSelected(null)}
          eyebrow={region ? `Region ${region.n} of ${REGION_COUNT}` : undefined}
          title={region?.name ?? ""}
          footer={
            <p className="text-xl text-white/40">
              Press <span className="font-mono text-white/60">]</span> for next region ·{" "}
              <span className="font-mono text-white/60">[</span> for previous
            </p>
          }
        >
          {region?.callouts ? (
            <ul className="space-y-5 text-xl leading-relaxed">
              {region.callouts.map((c) => (
                <li key={c.label}>
                  <span className="font-semibold text-white">{c.label}</span>
                  <span className="text-white/80"> — {c.text}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p>{region?.body}</p>
          )}
        </ExplainerPanel>

        {/* Closing beat — all nine regions explored */}
        <AnimatePresence>
          {active && allVisited && selected === null && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.4 }}
              className="absolute inset-0 z-40 flex items-center justify-center rounded-2xl bg-ink-950/70 backdrop-blur-sm"
            >
              <motion.div
                initial={{ y: 28, scale: 0.96 }}
                animate={{ y: 0, scale: 1 }}
                transition={{ duration: 0.5, ease: [0.32, 0.72, 0, 1] }}
              >
                <GlassCard glow className="max-w-3xl p-12 text-center">
                  <p className="font-display text-4xl font-semibold leading-snug text-white">
                    Measure business outcomes — qualified traffic, signups, revenue —{" "}
                    <GradientText>
                      not a single position for a vanity keyword.
                    </GradientText>
                  </p>
                  <button
                    type="button"
                    onClick={() => setVisited(new Set())}
                    className="mt-9 rounded-full border border-white/15 bg-white/5 px-7 py-3 text-xl text-white/70 transition hover:scale-105 hover:bg-white/10 hover:text-white"
                  >
                    Explore again
                  </button>
                </GlassCard>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </SlideShell>
  );
}
